import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Meeting } from "../../../shared/src/types/Meeting";
import MeetingDateSearch from "../components/MeetingDateSearch";
import {
  useGetMeetingsQuery,
  useUpdateMeetingMutation,
  useDeleteMeetingMutation,
} from "../api/MeetingsApi";
import toast from "react-hot-toast";

interface MeetingHistoryProps {
  familyId: string;
}

const MeetingHistory: React.FC<MeetingHistoryProps> = ({ familyId }) => {
  const navigate = useNavigate();
  const [inputDate, setInputDate] = useState("");
  const [filterDate, setFilterDate] = useState<string | undefined>(undefined);
  const { data: meetings = [], isLoading, isError } = useGetMeetingsQuery({
    familyId,
    dateFilter: filterDate,
  });

  const [deleteMeeting] = useDeleteMeetingMutation();
  const [updateMeeting] = useUpdateMeetingMutation();

  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [editableFields, setEditableFields] = useState<Record<string, Partial<Meeting>>>({});

  useEffect(() => {
    if (inputDate.length === 10) {
      const handler = setTimeout(() => {
        setFilterDate(inputDate);
      }, 500);
      return () => clearTimeout(handler);
    } else {
      setFilterDate(undefined);
    }
  }, [inputDate]);

  const handleEditClick = (meeting: Meeting) => {
    setEditingMeetingId(meeting.id);
    setEditableFields((prev) => ({
      ...prev,
      [meeting.id]: {
        location: meeting.location,
        date: meeting.date,
      },
    }));
  };

  const handleFieldChange = (meetingId: string, field: keyof Meeting, value: any) => {
    setEditableFields((prev) => ({
      ...prev,
      [meetingId]: {
        ...prev[meetingId],
        [field]: value,
      },
    }));
  };

  const handleSaveClick = async (meetingId: string) => {
    try {
      const updatedData = editableFields[meetingId];
      await updateMeeting({ id: meetingId, updatedData }).unwrap();
      toast.success("✅ הפגישה עודכנה בהצלחה", {
        position: "top-center",
        duration: 3000,
      });
      setEditingMeetingId(null);
    } catch (error) {
      console.error("שגיאה בעדכון הפגישה", error);
      toast.error("❌ שגיאה בעדכון הפגישה");
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("האם אתה בטוח שברצונך למחוק את הפגישה?")) {
      try {
        await deleteMeeting(id).unwrap();
        toast.success("הפגישה נמחקה בהצלחה");
      } catch (error) {
        toast.error("שגיאה במחיקת הפגישה");
      }
    }
  };

  const shouldShowNoResults = filterDate !== undefined && meetings.length === 0;
  const shouldShowIncompleteDateMessage = inputDate.length > 0 && inputDate.length < 10;

  return (
    <div className="mt-6 w-full overflow-x-auto max-w-full">
      <h2 className="text-xl font-bold mb-4 text-right">היסטוריית פגישות</h2>

      <MeetingDateSearch value={inputDate} onDateSelect={setInputDate} />

      {shouldShowIncompleteDateMessage && (
        <p className="text-center text-gray-500 mt-2">אנא השלם את התאריך</p>
      )}

      {isLoading ? (
        <p className="text-center text-blue-500 mt-4">טוען פגישות...</p>
      ) : isError ? (
        <p className="text-center text-red-500 mt-4">שגיאה בטעינת הפגישות.</p>
      ) : shouldShowNoResults ? (
        <p className="text-center text-gray-500 mt-4">לא נמצאו פגישות בתאריך הזה.</p>
      ) : (
        <table className="min-w-full bg-white border border-gray-300 rounded-md shadow">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-right">מס׳ פגישה</th>
              <th className="px-4 py-2 text-right">תאריך</th>
              <th className="px-4 py-2 text-right">מיקום</th>
              <th className="px-4 py-2 text-right">פעולות</th>
            </tr>
          </thead>
          <tbody>
            {meetings.map((meeting) => {
              const isEditing = editingMeetingId === meeting.id;
              const edited = editableFields[meeting.id] || {};
              return (
                <tr key={meeting.id} className="border-t hover:bg-gray-50 transition">
                  <td className="px-4 py-2 text-center">{meeting.meeting_number}</td>
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <input
                        type="date"
                        className="border rounded px-2 py-1 text-center"
                        value={
                          edited.date
                            ? new Date(edited.date).toISOString().split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange(
                            meeting.id,
                            "date",
                            new Date(e.target.value).toISOString()
                          )
                        }
                      />
                    ) : (
                      new Date(meeting.date).toLocaleDateString("he-IL")
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <input
                        className="border rounded px-2 py-1 text-center"
                        value={edited.location ?? ""}
                        onChange={(e) =>
                          handleFieldChange(meeting.id, "location", e.target.value)
                        }
                      />
                    ) : (
                      meeting.location
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex flex-wrap justify-center items-center gap-2">
                      <button
                        onClick={() => navigate(`/meetings/${meeting.id}`)}
                        className="min-w-[5rem] bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition"
                      >
                        צפייה
                      </button>
                      {isEditing ? (
                        <button
                          onClick={() => handleSaveClick(meeting.id)}
                          className="min-w-[5rem] bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition"
                        >
                          שמירה
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditClick(meeting)}
                          className="min-w-[5rem] bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition"
                        >
                          עריכה
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(meeting.id)}
                        className="min-w-[5rem] bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition"
                      >
                        מחיקה
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MeetingHistory;
